import { useCallback, useMemo } from 'react';
import _get from 'lodash/get';
import _pick from 'lodash/pick';
import { useLocalStorage } from 'usehooks-ts';
import {
  Task,
  TaskScopeNameEnum,
  TaskTimeline,
  TaskTimelineCompletionGroupByEnum,
  TaskTimelineFrameEnum,
  TaskTimelineReportCreateMutationResult,
  useTaskTimelineReportCreateMutation,
} from 'graphql-common';
import usePaginationParams from '@lib/hooks/usePaginationParams';
import useSearchParam from '@lib/hooks/useSearchParam';
import { TaskCounter } from '@lib/interfaces/task';
import { ErrorLike } from '@apollo/client';
import {
  DASHBOARD_TIMELINE_FILTER_FORM,
  GENERATE_REPORT_FORM,
} from '@lib/enums/localStorageKeys';
import { GetUrlParams } from '@lib/enums/urls';
import { getFormattedDate, transformDate } from '@lib/utils/dateTimeHelpers';
import { dateFormatForApi } from '@lib/enums/dateTime';
import { Value, Values } from '@lib/interfaces/form';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import useDashboardTimelineTasksQueryHook from 'utils/fetch/hooks/useDashboardTimelineTasksQueryHook';
import useDashboardTasksCompletionsQueryHook from 'utils/fetch/hooks/useDashboardTasksCompletionsQueryHook';
import { useWhoamiContext } from 'hooks/useWhoamiContext';
import { FieldNames } from 'components/GenerateReportForm/types';
import useGenerateReportActions from './useGenerateReportActions';
import { TASKS_TIMELINE_FILTER_EMPTY_VALUES } from './useFilterFields';

export type UseActionsResult = {
  basePeriodEndParam: string;
  basePeriodStartParam: string;
  endDateParam: string | null | undefined;
  filterParams: Values;
  isPeriodCustomized: boolean;
  maxDate?: string;
  minDate?: string;
  onEndDateChange: (v: string) => void;
  onReportCreateAction: (values: Values) => void;
  onUpdateFilterParams: (values: Values) => void;
  onViewChange: (v: unknown) => void;
  periodEndParam: string | null | undefined;
  periodStartParam: string | null | undefined;
  resetAllFilterParams: () => void;
  resetFilterParams: () => void;
  resetPeriodFilterParams: () => void;
  taskCounterData: TaskCounter;
  taskTimelineReportCreateMutationResult: TaskTimelineReportCreateMutationResult;
  tasks: Task[];
  tasksError?: ErrorLike;
  tasksFirstLoading: boolean;
  tasksLoading: boolean;
  timelineData?: TaskTimeline[];
  timelineGroupBy?: TaskTimelineFrameEnum;
  timelineLoading: boolean;
  totalTasksCount: number;
  view: TaskTimelineFrameEnum;
};

function getDataFromFilterParams(
  filterParams: Values,
  companyCreatedAt: string,
) {
  const view = (filterParams[TasksFilterFieldNames.TimelineView] ||
    TASKS_TIMELINE_FILTER_EMPTY_VALUES[
      TasksFilterFieldNames.TimelineView
    ]) as TaskTimelineFrameEnum;
  const minDate = companyCreatedAt;
  const maxDate = new Date().toISOString();
  const scopeName = (filterParams[TasksFilterFieldNames.ScopeName] ||
    TaskScopeNameEnum.DashboardAllGrouped) as string;
  const basePeriod = filterParams[TasksFilterFieldNames.Period] as string[];
  const basePeriodStartParam = basePeriod?.[0] || companyCreatedAt;
  const basePeriodEndParam = basePeriod?.[1] || maxDate;
  const periodStartParam = getFormattedDate(
    basePeriodStartParam,
    dateFormatForApi,
  );
  const periodEndParam = getFormattedDate(basePeriodEndParam, dateFormatForApi);
  // Falls back to `basePeriodEndParam` (CET-anchored, revertTransformDate-safe),
  // not `periodEndParam` (a bare `yyyy-MM-dd` API date) — `endDateParam` feeds
  // the timeline DatePicker/date math, which parses via `revertTransformDate`.
  const endDateParam =
    (filterParams[TasksFilterFieldNames.TimelineEndDate] as string) ||
    basePeriodEndParam;

  const preparedFilters = { ...filterParams };
  const periodFilter = preparedFilters[TasksFilterFieldNames.Period];
  const isPeriodFilterFilled =
    Array.isArray(periodFilter) && periodFilter?.[0] && periodFilter?.[1];
  if (!isPeriodFilterFilled) {
    preparedFilters[TasksFilterFieldNames.Period] = [
      basePeriodStartParam,
      basePeriodEndParam,
    ];
  }

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    endDateParam,
    isPeriodCustomized: !!isPeriodFilterFilled,
    maxDate,
    minDate,
    periodEndParam,
    periodStartParam,
    preparedFilters,
    scopeName,
    view,
  };
}

export default function useActions({
  useModalManagementResult,
}: {
  useModalManagementResult: UseModalManagementResult;
}): UseActionsResult {
  const [searchQuery] = useSearchParam();
  const [filterParams, setFilterParams] = useLocalStorage<{
    [key: string]: Value;
  }>(DASHBOARD_TIMELINE_FILTER_FORM, TASKS_TIMELINE_FILTER_EMPTY_VALUES);
  const [generateReportForm] = useLocalStorage<{
    [key: string]: Value;
  }>(GENERATE_REPORT_FORM, {});
  const [paginationParams, setPaginationParams] = usePaginationParams();

  // Whoami — reads ProtectedRoute's single query result. See useWhoamiContext.
  const { currentUser } = useWhoamiContext();

  // Data
  const companyCreatedAt = _get(currentUser, 'company.createdAt');
  const {
    basePeriodEndParam,
    basePeriodStartParam,
    endDateParam,
    isPeriodCustomized,
    maxDate,
    minDate,
    periodEndParam,
    periodStartParam,
    preparedFilters,
    scopeName,
    view,
  } = useMemo(
    () => getDataFromFilterParams(filterParams, companyCreatedAt),
    [filterParams, companyCreatedAt],
  );

  // Tasks
  const {
    taskCounterData,
    taskIds,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    totalTasksCount,
  } = useDashboardTimelineTasksQueryHook({
    searchQuery,
    filterParams: preparedFilters,
    paginationParams,
    scopeName,
  });
  const {
    loading: timelineLoading,
    data: timelineData,
    dataGroupBy: timelineGroupBy,
  } = useDashboardTasksCompletionsQueryHook({
    tasks,
    filterParams,
    loading: tasksLoading,
  });

  // Reporting
  const [
    taskTimelineReportCreateMutation,
    taskTimelineReportCreateMutationResult,
  ] = useTaskTimelineReportCreateMutation();
  const { onReportCreateAction } = useGenerateReportActions({
    onCompleted: () => {
      useModalManagementResult.closeAllModals();
    },
    taskTimelineReportCreateMutation: taskTimelineReportCreateMutation as (
      v: object,
    ) => Promise<unknown>,
    taskIds,
    filterParams: preparedFilters,
    completionGroupBy: _get(
      generateReportForm,
      `values.${FieldNames.CompletionGroupBy}.value`,
      TaskTimelineCompletionGroupByEnum.SiteArea,
    ) as TaskTimelineCompletionGroupByEnum,
    basePeriodStartParam,
    basePeriodEndParam,
  });

  const onViewChange = useCallback(
    (option: unknown) => {
      const newView = _get(option, 'value');
      if (newView) {
        // Preserve user's existing period selection when changing view
        const currentPeriod = preparedFilters[TasksFilterFieldNames.Period] as
          string[] | undefined;
        setFilterParams({
          ...preparedFilters,
          [TasksFilterFieldNames.TimelineView]: newView,
          [TasksFilterFieldNames.TimelineEndDate]: basePeriodEndParam,
          [TasksFilterFieldNames.Period]: currentPeriod || [
            basePeriodStartParam,
            basePeriodEndParam,
          ],
        });
      }
    },
    [
      setFilterParams,
      preparedFilters,
      basePeriodEndParam,
      basePeriodStartParam,
    ],
  );

  const onEndDateChange = useCallback(
    (date: string) => {
      if (date) {
        // Preserve user's existing period selection during timeline navigation
        const currentPeriod = filterParams[TasksFilterFieldNames.Period] as
          string[] | undefined;
        setFilterParams({
          ...filterParams,
          [TasksFilterFieldNames.TimelineEndDate]: date,
          [TasksFilterFieldNames.Period]: currentPeriod || [
            basePeriodStartParam,
            basePeriodEndParam,
          ],
        });
      }
    },
    [setFilterParams, filterParams, basePeriodStartParam, basePeriodEndParam],
  );

  const resetFilterParams = useCallback(() => {
    setFilterParams({
      ...TASKS_TIMELINE_FILTER_EMPTY_VALUES,
      ..._pick(
        preparedFilters,
        TasksFilterFieldNames.Period,
        TasksFilterFieldNames.TimelineEndDate,
      ),
    });
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  }, [preparedFilters, paginationParams, setFilterParams, setPaginationParams]);

  const resetPeriodFilterParams = useCallback(() => {
    const nowDateForReset = new Date();
    // CET-anchored (transformDate), not a bare `yyyy-MM-dd`: this value feeds
    // the picker/date math via `revertTransformDate`, which mis-parses a bare
    // date as local midnight and drops a day for timezones east of CET.
    const periodEndDate = transformDate(nowDateForReset, true);
    setFilterParams({
      ...preparedFilters,
      [TasksFilterFieldNames.Period]: [companyCreatedAt, periodEndDate],
      [TasksFilterFieldNames.TimelineEndDate]: periodEndDate,
    });
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  }, [
    companyCreatedAt,
    preparedFilters,
    paginationParams,
    setFilterParams,
    setPaginationParams,
  ]);

  const resetAllFilterParams = useCallback(() => {
    setFilterParams(TASKS_TIMELINE_FILTER_EMPTY_VALUES);
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  }, [paginationParams, setFilterParams, setPaginationParams]);

  const onUpdateFilterParams = (values: Values) => {
    setFilterParams(values);
    setPaginationParams({ ...paginationParams, [GetUrlParams.Page]: '1' });
  };

  return {
    basePeriodEndParam,
    basePeriodStartParam,
    endDateParam,
    filterParams: preparedFilters,
    isPeriodCustomized,
    maxDate,
    minDate,
    onEndDateChange,
    onReportCreateAction,
    onUpdateFilterParams,
    onViewChange,
    periodEndParam,
    periodStartParam,
    resetAllFilterParams,
    resetFilterParams,
    resetPeriodFilterParams,
    taskCounterData,
    taskTimelineReportCreateMutationResult,
    tasks,
    tasksError,
    tasksFirstLoading,
    tasksLoading,
    timelineData,
    timelineGroupBy,
    timelineLoading,
    totalTasksCount,
    view,
  };
}
